import { graphql } from '@eluve/graphql.tada';

import { appointmentAmendments } from './amendment.operations';
import {
  appointmentActivityFragment,
  appointmentLlmJobFragment,
} from './useAppointmentActivity';
import { appointmentHeaderDetailsFragment } from './useAppointmentHeaderDetails';
import { appointmentModeFragment } from './useAppointmentMode';
import { appointmentStatusFragment } from './useAppointmentStatus';
import { appointmentTotalDurationFragment } from './useAppointmentTotalDuration';

export const createAppointmentMutation = graphql(`
  mutation insertAnonymousAppointment(
    $startDate: timestamptz!
    $name: String
    $locationId: String
  ) {
    insertAppointmentsOne(
      object: { startDate: $startDate, name: $name, locationId: $locationId }
    ) {
      __typename
      id
      tenantId
      name
      locationId
    }
  }
`);

export const externalChartFragment = graphql(`
  fragment externalChart on ExternalCharts @_unmask {
    __typename
    id
    signedAt
    chartUrl
    externalChartId
    externalPatientId
    externalAppointmentId
    appointmentId
    patientId
  }
`);

export const externalChartTemplateRefFragment = graphql(`
  fragment ExternalChartTemplateRef on ExternalChartTemplates @_unmask {
    __typename
    id
    externalChartTemplateId
  }
`);

export const outputTemplateMetadataFragment = graphql(
  `
    fragment OutputTemplateMetadata on LlmOutputsMetadata @_unmask {
      __typename
      llmOutputId
      tenantId
      output_template {
        __typename
        id
        name
        llmOutputType
        external_chart_templates {
          ...ExternalChartTemplateRef
        }
      }
      output_variant {
        __typename
        outputTemplateId
        variantNumber
        template
      }
    }
  `,
  [externalChartTemplateRefFragment],
);

export const promptTemplateMetadataFragment = graphql(`
  fragment PromptTemplateMetadata on LlmOutputsMetadata @_unmask {
    __typename
    llmOutputId
    tenantId
    prompt_template {
      __typename
      id
      name
      outputType
    }
    prompt_variant {
      __typename
      promptTemplateId
      variantNumber
      template
    }
  }
`);

export const externalChartsFragment = graphql(
  `
    fragment externalCharts on Appointments @_unmask {
      __typename
      id
      external_charts {
        ...externalChart
      }
    }
  `,
  [externalChartFragment],
);

export const latestHumanSOAPNoteFragment = graphql(
  `
    fragment LatestHumanSOAPNote on Appointments @_unmask {
      __typename
      id
      humanOutputs(
        where: { output: { outputType: { _eq: SOAP_NOTE } } }
        limit: 1
        orderBy: { output: { updatedAt: DESC } }
      ) {
        __typename
        humanOutputId
        appointmentId
        output {
          __typename
          id
          content
          userId
          editedFromLlmOutputId
          createdAt
          updatedAt
          llm_output {
            __typename
            id
            metadata {
              ...OutputTemplateMetadata
              prompt_template {
                __typename
                id
                outputType
              }
            }
          }
        }
        output_template {
          __typename
          outputTemplateId
          variantNumber
          template
          llm_output_template {
            __typename
            id
            external_chart_templates {
              ...ExternalChartTemplateRef
            }
          }
        }
      }
    }
  `,
  [outputTemplateMetadataFragment, externalChartTemplateRefFragment],
);

export const latestHumanSOAPNoteWithPromptTemplateFragment = graphql(
  `
    fragment LatestHumanSOAPNoteWithPromptTemplate on Appointments @_unmask {
      __typename
      id
      template_bundle {
        __typename
        id
        displayName
      }
      humanOutputs(
        where: { output: { outputType: { _eq: SOAP_NOTE } } }
        limit: 1
        orderBy: { output: { updatedAt: DESC } }
      ) {
        __typename
        humanOutputId
        appointmentId
        output {
          __typename
          id
          content
          userId
          editedFromLlmOutputId
          createdAt
          updatedAt
          llm_output {
            __typename
            id
            startedAt
            completedAt
            modelPromptTemplate {
              __typename
              id
              model_args {
                __typename
                id
                modelType
                args
              }
            }
            metadata {
              ...PromptTemplateMetadata
              ...OutputTemplateMetadata
            }
          }
        }
        output_template {
          __typename
          outputTemplateId
          variantNumber
          template
          llm_output_template {
            __typename
            id
            external_chart_templates {
              ...ExternalChartTemplateRef
            }
          }
        }
      }
    }
  `,
  [
    promptTemplateMetadataFragment,
    outputTemplateMetadataFragment,
    externalChartTemplateRefFragment,
  ],
);

export const appointmentBatchTranscriptFragment = graphql(`
  fragment appointmentBatchTranscription on Appointments @_unmask {
    __typename
    id
    transcripts(where: { transcriptType: { _in: [BATCH, CHUNKED_BATCH] } }) {
      __typename
      id
      segmentId
      transcriptionSource
      transcriptionStatus
      updatedAt
    }
  }
`);

export const appointmentTranscriptFragment = graphql(`
  fragment appointmentTranscription on Appointments @_unmask {
    __typename
    id
    transcription {
      __typename
      id
      transcript
      updatedAt
    }
  }
`);

export const getLatestTranscriptQuery = graphql(
  `
    query getLatestTranscript($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        startDate
        ...appointmentTranscription
        ...appointmentBatchTranscription
      }
    }
  `,
  [appointmentTranscriptFragment, appointmentBatchTranscriptFragment],
);

export const appointmentSegmentsFragment = graphql(`
  fragment appointmentSegments on Appointments @_unmask {
    __typename
    id
    segments {
      __typename
      id
      transcript
      recordingStartedAt
      recordingDuration
      updatedAt
    }
  }
`);

export const appointmentReadonlyDataFragment = graphql(`
  fragment ReadonlyData on Appointments {
    __typename
    id
    userId
    is_closed_for_modification
  }
`);

export const appointmentTranscriptAndSegmentsFragment = graphql(
  `
    fragment appointmentTranscriptionAndSegments on Appointments @_unmask {
      __typename
      id
      ...appointmentSegments
      ...appointmentTranscription
    }
  `,
  [appointmentSegmentsFragment, appointmentTranscriptFragment],
);

export const getCurrentSummary = graphql(
  `
    query getCurrentSummary($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        ...LatestHumanSOAPNote
      }
    }
  `,
  [latestHumanSOAPNoteFragment],
);

export const allCompletedMedicalNotesFragment = graphql(
  `
    fragment AllCompletedMedicalNotes on Appointments @_unmask {
      __typename
      id
      appointment_llm_outputs(
        where: {
          output: {
            _and: [
              { status: { _eq: COMPLETED } }
              {
                _or: [
                  {
                    modelPromptTemplate: {
                      template: {
                        outputType: { _in: [SOAP_NOTE, DYNAMIC_OUTPUT] }
                      }
                    }
                  }
                  {
                    outputTemplate: {
                      llmOutputType: { _in: [SOAP_NOTE, DYNAMIC_OUTPUT] }
                    }
                  }
                ]
              }
            ]
          }
        }
        orderBy: { output: { completedAt: ASC_NULLS_LAST } }
      ) {
        __typename
        llmOutputId
        output {
          __typename
          id
          content
          status
          completedAt
          modelPromptTemplate {
            __typename
            id
          }
          metadata {
            ...PromptTemplateMetadata
            ...OutputTemplateMetadata
          }
        }
      }
    }
  `,
  [promptTemplateMetadataFragment, outputTemplateMetadataFragment],
);

export const getCurrentSummaryForEluveAdmin = graphql(
  `
    query getCurrentSummaryForEluveAdmin(
      $tenantId: uuid!
      $appointmentId: uuid!
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        ...LatestHumanSOAPNoteWithPromptTemplate
      }
    }
  `,
  [latestHumanSOAPNoteWithPromptTemplateFragment],
);

export const getAllSummaries = graphql(
  `
    query getAllSummaries($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        ...AllCompletedMedicalNotes
      }
    }
  `,
  [allCompletedMedicalNotesFragment],
);

export const allAppointmentLlmSummariesWithMetadataFragment = graphql(
  `
    fragment AllAppointmentLlmSummariesWithMetadata on Appointments @_unmask {
      __typename
      id
      appointment_llm_outputs(
        where: {
          output: {
            _and: [
              { status: { _in: [COMPLETED, FAILED, IN_PROGRESS, CANCELLED] } }
              {
                _or: [
                  {
                    modelPromptTemplate: {
                      template: {
                        outputType: { _in: [SOAP_NOTE, DYNAMIC_OUTPUT] }
                      }
                    }
                  }
                  {
                    outputTemplate: {
                      llmOutputType: { _in: [SOAP_NOTE, DYNAMIC_OUTPUT] }
                    }
                  }
                ]
              }
            ]
          }
        }
        orderBy: { output: { createdAt: DESC } }
      ) {
        __typename
        llmOutputId
        job {
          __typename
          jobId
          jobStatus
        }
        output {
          __typename
          id
          content
          status
          failureType
          startedAt
          completedAt
          createdAt
          modelPromptTemplate {
            __typename
            id
            model_args {
              __typename
              id
              modelType
              args
            }
            template {
              __typename
              id
              name
              outputType
            }
          }
          metadata {
            ...PromptTemplateMetadata
            ...OutputTemplateMetadata
          }
        }
      }
    }
  `,
  [promptTemplateMetadataFragment, outputTemplateMetadataFragment],
);

export const getAllSummariesForEluveAdmin = graphql(
  `
    query getAllSummariesForEluveAdmin(
      $tenantId: uuid!
      $appointmentId: uuid!
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        humanOutputs(limit: 1, orderBy: { output: { updatedAt: DESC } }) {
          __typename
          humanOutputId
          output {
            __typename
            id
            editedFromLlmOutputId
          }
        }
        ...AllAppointmentLlmSummariesWithMetadata
      }
    }
  `,
  [allAppointmentLlmSummariesWithMetadataFragment],
);

export const getLatestComparisonExperiment = graphql(`
  query getLatestComparisonExperiment($tenantId: uuid!, $appointmentId: uuid!) {
    appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
      __typename
      id
      appointment_llm_jobs(
        where: { experiments: {} }
        orderBy: { createdAt: DESC }
        limit: 1
      ) {
        __typename
        jobId
        createdAt
        experiments(orderBy: { createdAt: DESC }, limit: 1) {
          __typename
          appointmentLlmJobId
          comparisonExperimentId
          selectedOutputId
          selectedBy
          selectedAt
        }
      }
    }
  }
`);

export const getComparisonJobOutputs = graphql(
  `
    query getComparisonJobOutputs(
      $tenantId: uuid!
      $appointmentId: uuid!
      $jobId: uuid!
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        appointment_llm_outputs(
          where: { jobId: { _eq: $jobId } }
          orderBy: { output: { startedAt: ASC } }
        ) {
          __typename
          llmOutputId
          output {
            __typename
            id
            content
            status
            completedAt
            startedAt
            metadata {
              ...OutputTemplateMetadata
            }
          }
        }
      }
    }
  `,
  [outputTemplateMetadataFragment],
);

export const recordExperimentAndUpdateNote = graphql(`
  mutation recordExperimentAndUpdateNote(
    $tenantId: uuid!
    $appointmentId: uuid!
    $jobId: uuid!
    $selectedOutputId: uuid!
    $selectedBy: uuid!
    $selectedAt: timestamptz!
    $humanOutputId: uuid!
    $content: jsonb!
  ) {
    updateAppointmentLlmJobExperiments(
      where: {
        tenantId: { _eq: $tenantId }
        appointmentId: { _eq: $appointmentId }
        appointmentLlmJobId: { _eq: $jobId }
      }
      _set: {
        selectedOutputId: $selectedOutputId
        selectedBy: $selectedBy
        selectedAt: $selectedAt
      }
    ) {
      affectedRows
    }
    updateHumanOutputsByPk(
      pkColumns: { tenantId: $tenantId, id: $humanOutputId }
      _set: { content: $content, editedFromLlmOutputId: $selectedOutputId }
    ) {
      __typename
      id
      content
      editedFromLlmOutputId
    }
  }
`);

export const updateSoapNote = graphql(`
  mutation updateSoapNote(
    $tenantId: uuid!
    $humanOutputId: uuid!
    $content: jsonb!
    $llmOutputId: uuid!
  ) {
    updateHumanOutputsByPk(
      pkColumns: { tenantId: $tenantId, id: $humanOutputId }
      _set: { content: $content, editedFromLlmOutputId: $llmOutputId }
    ) {
      __typename
      id
      content
      editedFromLlmOutputId
    }
  }
`);

export const updateAppointmentPromptTemplateId = graphql(`
  mutation updateAppointmentPromptTemplateId(
    $tenantId: uuid!
    $appointmentId: uuid!
    $initialPromptTemplateId: uuid!
  ) {
    updateAppointmentsByPk(
      pkColumns: { tenantId: $tenantId, id: $appointmentId }
      _set: { initialPromptTemplateId: $initialPromptTemplateId }
    ) {
      id
      __typename
      initialPromptTemplateId
    }
  }
`);

export const appointmentTemplateBundleFragment = graphql(`
  fragment AppointmentTemplateBundle on Appointments @_unmask {
    __typename
    id
    initialTemplateBundleId
  }
`);

export const updateAppointmentTemplateBundleMutation = graphql(`
  mutation UpdateAppointmentTemplateBundle(
    $tenantId: uuid!
    $id: uuid!
    $initialTemplateBundleId: uuid
    $initialPromptTemplateId: uuid
    $initialLlmOutputTemplateId: uuid
  ) {
    updateAppointmentsByPk(
      pkColumns: { tenantId: $tenantId, id: $id }
      _set: {
        initialTemplateBundleId: $initialTemplateBundleId
        initialPromptTemplateId: $initialPromptTemplateId
        initialLlmOutputTemplateId: $initialLlmOutputTemplateId
      }
    ) {
      __typename
      id
      initialTemplateBundleId
      initialPromptTemplateId
      initialLlmOutputTemplateId
    }
  }
`);

export const updateAppointmentOutputTemplateId = graphql(`
  mutation updateAppointmentOutputTemplateId(
    $tenantId: uuid!
    $appointmentId: uuid!
    $initialLlmOutputTemplateId: uuid
  ) {
    updateAppointmentsByPk(
      pkColumns: { tenantId: $tenantId, id: $appointmentId }
      _set: { initialLlmOutputTemplateId: $initialLlmOutputTemplateId }
    ) {
      id
      __typename
      initialLlmOutputTemplateId
    }
  }
`);

export const appointmentLlmOutputTemplateFragment = graphql(
  `
    fragment llmOutputTemplate on Appointments @_unmask {
      llm_output_template {
        id
        __typename
        name
        external_chart_templates {
          ...ExternalChartTemplateRef
        }
      }
    }
  `,
  [externalChartTemplateRefFragment],
);

export const appointmentExternalInfoFragment = graphql(`
  fragment appointmentExternalInfo on Appointments @_unmask {
    __typename
    id
    external_appointments_infos {
      __typename
      id
      externalAppointmentId
      ehr {
        __typename
        id
        vendor
      }
    }
  }
`);

export const baseAppointmentInfoFragment = graphql(
  `
    fragment baseAppointmentInfo on Appointments @_unmask {
      name
      startDate
      startedAt
      endDate
      completedAt
      locationId
      initialPromptTemplateId
      initialLlmOutputTemplateId
      initialTemplateBundleId
      patientId
      userId
      inputLanguage
      outputLanguage
      totalDuration
      updatedAt
      lastInteractionDate
      mode
      patientReasonForVisit
      chiefComplaint
      session_type {
        __typename
        id
        name
      }
      ...appointmentExternalInfo
    }
  `,
  [appointmentExternalInfoFragment],
);

export const AppointmentPatientIdFragment = graphql(`
  fragment AppointmentPatientId on Appointments {
    __typename
    id
    patientId
  }
`);

export const patientOverviewFragment = graphql(`
  fragment PatientOverview on PatientOverviews @_unmask {
    id
    __typename
    isCurrent
    createdAt
    content
  }
`);

export const basicPatientDetailsFragment = graphql(`
  fragment basicDetails on Patients @_unmask {
    __typename
    id
    firstName
    lastName
    dateOfBirth
    sex
    email
  }
`);

export const patientFragment = graphql(
  `
    fragment patient on Patients @_unmask {
      __typename
      ...basicDetails
      patient_overviews(orderBy: { createdAt: DESC }) {
        ...PatientOverview
      }
      external_patients_info {
        __typename
        id
        externalPatientId
        cellPhoneNumber
        homePhoneNumber
        workPhoneNumber
        email
        external_ehr {
          __typename
          id
          vendor
          domain
        }
      }
    }
  `,
  [patientOverviewFragment, basicPatientDetailsFragment],
);

export const appointmentPatientFragment = graphql(
  `
    fragment appointmentPatient on Appointments @_unmask {
      patient {
        ...patient
      }
    }
  `,
  [patientFragment],
);

export const doctorInteractionFragment = graphql(`
  fragment doctorInteraction on AppointmentDoctorInteractions @_unmask {
    __typename
    appointmentId
    additionalNotes
    noteSignedAt
    externalChartUrl
    updatedAt
    user {
      __typename
      id
      firstName
      lastName
    }
    appointment {
      __typename
      id
      is_closed_for_modification
    }
  }
`);

export const appointmentDoctorInteractionFragment = graphql(
  `
    fragment appointmentDoctorInteraction on Appointments @_unmask {
      __typename
      id
      doctor_interaction {
        ...doctorInteraction
      }
    }
  `,
  [doctorInteractionFragment],
);

export const appointmentLocationFragment = graphql(`
  fragment appointmentLocation on Appointments @_unmask {
    locationId
    location {
      __typename
      id
      name
    }
  }
`);

export const appointmentLanguagesFragment = graphql(`
  fragment AppointmentLanguages on Appointments @_unmask {
    __typename
    id
    inputLanguage
    outputLanguage
  }
`);

export const latestSignedBillingCodesFragment = graphql(`
  fragment LatestSignedBillingCodes on Appointments @_unmask {
    __typename
    id

    latest_signed_billing_codes {
      __typename
      id
      medical_code {
        __typename
        code
        codeType
        description
      }
    }
  }
`);

export const appointmentConversationsFragment = graphql(`
  fragment appointmentConversations on Appointments @_unmask {
    id
    __typename
    appointment_conversations(
      where: { conversationType: { _eq: INTAKE } }
      orderBy: { createdAt: DESC }
    ) {
      __typename
      id
      conversation
      appointmentId
    }
  }
`);

const superbillFragment = graphql(`
  fragment Superbill on AppointmentFiles @_unmask {
    __typename
    tenantId
    appointmentId
    fileId
    fileType
    createdAt
    userUpload {
      __typename
      tenantId
      id
      user {
        __typename
        id
        firstName
        lastName
        email
      }
    }
  }
`);

export const appointmentSuperbillsFragment = graphql(
  `
    fragment AppointmentSuperbills on Appointments @_unmask {
      id
      __typename
      appointmentFiles(
        where: { fileType: { _eq: SUPERBILL_PDF } }
        orderBy: { createdAt: DESC }
      ) {
        ...Superbill
      }
    }
  `,
  [superbillFragment],
);

export const appointmentLatestSuperbillFragment = graphql(
  `
    fragment AppointmentLatestSuperbill on Appointments @_unmask {
      id
      __typename
      appointmentFiles(
        where: { fileType: { _eq: SUPERBILL_PDF } }
        orderBy: { createdAt: DESC }
        limit: 1
      ) {
        ...Superbill
      }
    }
  `,
  [superbillFragment],
);

export const activeAppointmentLayoutFragment = graphql(
  `
    fragment ActiveAppointmentLayout on Appointments {
      ...appointmentStatus
      ...baseAppointmentInfo
      ...appointmentPatient
      ...appointmentDoctorInteraction
      ...appointmentActiveState
      ...appointmentTranscriptionAndSegments
      ...appointmentLocation
      ...llmOutputTemplate
      ...ReadonlyData
      ...appointmentConversations
      ...AppointmentLatestSuperbill
    }
  `,
  [
    appointmentStatusFragment,
    baseAppointmentInfoFragment,
    appointmentPatientFragment,
    appointmentDoctorInteractionFragment,
    appointmentActivityFragment,
    appointmentTranscriptAndSegmentsFragment,
    appointmentLocationFragment,
    appointmentLlmOutputTemplateFragment,
    appointmentReadonlyDataFragment,
    appointmentConversationsFragment,
    appointmentLatestSuperbillFragment,
  ],
);

export const signedAppointmentLayoutFragment = graphql(
  `
    fragment SignedAppointmentLayout on Appointments {
      ...baseAppointmentInfo
      ...appointmentPatient
      ...appointmentDoctorInteraction
      ...externalCharts
      ...ReadonlyData
      ...AppointmentAmendments
      ...LatestSignedBillingCodes
      ...AppointmentLatestSuperbill
    }
  `,
  [
    baseAppointmentInfoFragment,
    appointmentPatientFragment,
    appointmentDoctorInteractionFragment,
    externalChartsFragment,
    appointmentReadonlyDataFragment,
    appointmentAmendments,
    latestSignedBillingCodesFragment,
    appointmentLatestSuperbillFragment,
  ],
);

export const appointmentNameFragment = graphql(`
  fragment AppointmentName on Appointments @_unmask {
    __typename
    id
    name
  }
`);

export const getAppointmentDetailsQuery = graphql(
  `
    query getAppointmentDetails($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        ...ActiveAppointmentLayout
        ...SignedAppointmentLayout
        ...AppointmentHeaderDetails
        ...AppointmentTemplateBundle
      }
    }
  `,
  [
    activeAppointmentLayoutFragment,
    signedAppointmentLayoutFragment,
    appointmentHeaderDetailsFragment,
    appointmentTemplateBundleFragment,
  ],
);

export const listenAppointmentStatusSubscription = graphql(
  `
    subscription listenAppointmentStatus(
      $tenantId: uuid!
      $appointmentId: uuid!
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        chiefComplaint
        totalDuration
        ...appointmentActiveState
        ...appointmentStatus
      }
    }
  `,
  [appointmentActivityFragment, appointmentStatusFragment],
);

export const listenAppointmentSummarizationProgressSubscription = graphql(
  `
    subscription listenAppointmentSummarizationProgress(
      $tenantId: uuid!
      $appointmentId: uuid!
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        isSummarizationInProgress
        isAnySummarizationInProgress
        isTranscriptionInProgress
        isPendingAudioFileUpload
        ...appointmentStatus
        ...AppointmentTemplateBundle
      }
    }
  `,
  [appointmentStatusFragment, appointmentTemplateBundleFragment],
);

export const listenAppointmentLlmJobsSubscription = graphql(
  `
    subscription listenAppointmentJobsStatus(
      $tenantId: uuid!
      $appointmentId: uuid!
      $initialValue: timestamptz!
    ) {
      appointmentLlmJobsStream(
        batchSize: 1
        cursor: { initialValue: { updatedAt: $initialValue }, ordering: ASC }
        where: {
          appointmentId: { _eq: $appointmentId }
          tenantId: { _eq: $tenantId }
        }
      ) {
        ...AppointmentLlmJob
      }
    }
  `,
  [appointmentLlmJobFragment],
);

export const getPatientDetailsQuery = graphql(
  `
    query getPatientDetails($tenantId: uuid!, $patientId: uuid!) {
      patientsByPk(tenantId: $tenantId, id: $patientId) {
        id
        __typename
        ...patient
      }
    }
  `,
  [patientFragment],
);

export const getAppointmentSuperbillsQuery = graphql(
  `
    query getAppointmentSuperbills($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        ...AppointmentSuperbills
      }
    }
  `,
  [appointmentSuperbillsFragment],
);

export const getAppointmentLatestSuperbillQuery = graphql(
  `
    query getAppointmentLatestSuperbill(
      $tenantId: uuid!
      $appointmentId: uuid!
    ) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        ...AppointmentLatestSuperbill
      }
    }
  `,
  [appointmentLatestSuperbillFragment],
);

export const getUserCompletedAppointmentsCountQuery = graphql(`
  query getUserCompletedAppointmentsCount($tenantId: uuid!, $userId: uuid!) {
    appointmentsAggregate(
      where: {
        tenantId: { _eq: $tenantId }
        userId: { _eq: $userId }
        status: { _eq: COMPLETED }
      }
    ) {
      __typename
      aggregate {
        __typename
        count
      }
    }
  }
`);

export const insertTranscriptMutation = graphql(`
  mutation insertTranscript(
    $tenantId: uuid!
    $appointmentId: uuid!
    $transcript: String!
    $segmentId: uuid!
  ) {
    insertAppointmentSegmentsOne(
      object: {
        appointmentId: $appointmentId
        id: $segmentId
        recordingStartedAt: NOW
      }
    ) {
      __typename
      id
      appointment {
        __typename
        id
        segments {
          __typename
          id
          transcript
        }
      }
    }

    insertTranscriptsOne(
      object: {
        appointmentId: $appointmentId
        transcript: $transcript
        transcriptionCompletedAt: NOW
        transcriptionStatus: COMPLETED
        segmentId: $segmentId
        transcriptType: BATCH
        isFinalChunk: true
      }
    ) {
      id
      transcriptionStatus
    }

    updateAppointmentsByPk(
      pkColumns: { tenantId: $tenantId, id: $appointmentId }
      _set: { status: ACTIVE }
    ) {
      __typename
      id
    }
  }
`);

export const getAppointmentBaseQuery = graphql(
  `
    query getCurrentAppointment($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        ...AppointmentHeaderDetails
        ...appointmentTotalDuration
        ...appointmentPatient
      }
    }
  `,
  [
    appointmentHeaderDetailsFragment,
    appointmentTotalDurationFragment,
    appointmentPatientFragment,
  ],
);

export const appointmentPatientNameFragment = graphql(`
  fragment AppointmentPatientName on Appointments @_unmask {
    __typename
    id
    patient {
      __typename
      id
      firstName
      lastName
    }
  }
`);

export const getAppointmentByExternalAppointmentId = graphql(
  `
    query getAppointmentByExternalAppointmentId(
      $tenantId: uuid!
      $externalAppointmentId: String!
    ) {
      externalAppointmentsInfo(
        where: {
          tenantId: { _eq: $tenantId }
          externalAppointmentId: { _eq: $externalAppointmentId }
        }
        limit: 1
      ) {
        __typename
        id
        appointment {
          __typename
          id
          ...AppointmentHeaderDetails
          ...appointmentActiveState
          ...AppointmentMode
          ...AppointmentPatientName
          ...appointmentTotalDuration
        }
      }
    }
  `,
  [
    appointmentHeaderDetailsFragment,
    appointmentActivityFragment,
    appointmentModeFragment,
    appointmentPatientNameFragment,
    appointmentTotalDurationFragment,
  ],
);
